import mixpanel from "mixpanel-browser"

const TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN
const API_HOST =
  import.meta.env.VITE_MIXPANEL_API_HOST ?? "https://api-eu.mixpanel.com"
const DEBUG = import.meta.env.VITE_MIXPANEL_DEBUG === "true"

let initialized = false

export const AnalyticsEvents = {
  SignIn: "Sign In",
  SignInFailed: "Sign In Failed",
  SignUp: "Sign Up",
  SignUpFailed: "Sign Up Failed",
  SignOut: "Sign Out",
  ChatMessageSent: "Chat Message Sent",
  ChatMessageFailed: "Chat Message Failed",
  JurisdictionSelected: "Jurisdiction Selected",
  ChatSelected: "Chat Selected",
  NewChatStarted: "New Chat Started",
  SidebarTabChanged: "Sidebar Tab Changed",
  ThemeChanged: "Theme Changed",
} as const

export type AnalyticsUser = {
  id: string
  email: string
  name?: string
}

export type IdentifySource = "login" | "register" | "session_restore"

export function initAnalytics(): void {
  if (initialized || !TOKEN) return

  mixpanel.init(TOKEN, {
    api_host: API_HOST,
    persistence: "localStorage",
    debug: DEBUG || import.meta.env.DEV,
    track_pageview: false,
    autocapture: {
      pageview: false,
      click: true,
      dead_click: true,
      rage_click: true,
      scroll: true,
      submit: true,
      input: false,
      capture_text_content: false,
    },
    ignore_dnt: false,
  })
  initialized = true
}

export function isAnalyticsEnabled(): boolean {
  return initialized && Boolean(TOKEN)
}

function safeCall(fn: () => void): void {
  if (!isAnalyticsEnabled()) return
  try {
    fn()
  } catch (error) {
    console.warn("[analytics]", error)
  }
}

export function identifyUser(user: AnalyticsUser, source?: IdentifySource): void {
  safeCall(() => {
    mixpanel.identify(user.id)
    mixpanel.people.set({
      $email: user.email,
      ...(user.name ? { $name: user.name } : {}),
      ...(source ? { last_auth_source: source } : {}),
    })
    mixpanel.register({
      user_id: user.id,
    })
  })
}

export function trackSignUp(properties?: Record<string, unknown>): void {
  safeCall(() => {
    mixpanel.people.set_once({
      signup_date: new Date().toISOString(),
    })
    mixpanel.track(AnalyticsEvents.SignUp, {
      method: "email",
      ...properties,
    })
  })
}

export function trackSignIn(properties?: Record<string, unknown>): void {
  trackEvent(AnalyticsEvents.SignIn, {
    method: "email",
    ...properties,
  })
}

export function resetAnalytics(): void {
  safeCall(() => {
    mixpanel.track(AnalyticsEvents.SignOut)
    mixpanel.reset()
  })
}

export function trackEvent(
  event: (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents] | string,
  properties?: Record<string, unknown>
): void {
  safeCall(() => {
    mixpanel.track(event, properties)
  })
}

export function timeEvent(
  event: (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents] | string
): void {
  safeCall(() => {
    mixpanel.time_event(event)
  })
}

export function trackPageView(pathname: string, search = ""): void {
  safeCall(() => {
    mixpanel.track_pageview({
      page: `${pathname}${search}`,
    })
  })
}
